// Shared ranking for the hint (pre-commit) and tutor (post-commit) modals.
// Sorts every legal play by points equity, then collapses plays that reach the
// same final position so rank numbers count distinct *moves*, not sub-move
// orderings: "7/3 6/3" and "6/3 7/3" are one move shown once, and the numbers
// the tutor modal prints ("your move was 14th best") mean what they say.

import { Play, applyPlay } from "../engine/moves";
import { Position, hashPosition } from "../engine/position";
import { winProbFromGameEquity } from "./hint-table";

export interface RankedPlay {
  play: Play;
  /** Gammon-aware points equity — the ranking metric. */
  equity: number;
  /** Probability of winning the game, 0..1. Undefined when game equity isn't finite. */
  winProb?: number;
  /** 1-based rank among distinct final positions. */
  rank: number;
  /** hashPosition(applyPlay(startPos, play)) — lets callers match a play by outcome. */
  finalHash: string;
}

/**
 * Rank `legalPlays` best-first, one row per distinct final position.
 * `pointsEquities` / `gameEquities` are parallel to `legalPlays` (the
 * MoveAnalysis contract — input order, one entry each).
 *
 * Named for the dedup to keep it distinct from `rankPlays` in ai/search.ts,
 * which ranks with the evaluator and keeps transpositions.
 */
export function rankDistinctPlays(
  startPos: Position,
  legalPlays: Play[],
  pointsEquities: number[],
  gameEquities: number[],
): RankedPlay[] {
  const indices = legalPlays.map((_, i) => i);
  indices.sort((a, b) => pointsEquities[b] - pointsEquities[a]);

  const seen = new Set<string>();
  const ranked: RankedPlay[] = [];
  for (const i of indices) {
    const finalHash = hashPosition(applyPlay(startPos, legalPlays[i]));
    if (seen.has(finalHash)) continue;
    seen.add(finalHash);
    ranked.push({
      play: legalPlays[i],
      equity: pointsEquities[i],
      winProb: winProbFromGameEquity(gameEquities[i]),
      rank: ranked.length + 1,
      finalHash,
    });
  }
  return ranked;
}
